import React, { useState } from "react";
import {
  DropLocationCaptureProperty,
  DropOverride,
  ELightPath,
  LipidPhaseDropLocatorCaptureProperty,
  LocateDropAlgorithmProperty,
  PlateTypeCaptureProfile,
  StandardDropLocatorCaptureProperty,
  VisibleCaptureProperty,
} from "../../../Model/CaptureProfile";
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import { PlateType } from "../../../Model/PlateType";
import ImagerUtil from "../../../Utility/ImagerUtil";
import ContinousZoomDropOverride from "./ContinuousZoomDropOverride";
import DefaultImagingSettingBinning from "./DefaultImagingSettingBinning";
import DefaultImagingSettingComboBox from "./DefaultImagingSettingComboBox";
import DefaultImagingSettingRadioButton from "./DefaultImagingSettingRadioButton";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import LcpDropLocator from "./LcpDropLocator";
import ObjectiveZoomDropOverride from "./ObjectiveZoomDropOverride";
import RoiOptions from "./RoiOptions";
import StandardDropLocatorView from "./StandardDropLocator";

const DROP_LOCATION_METHOD = "dropLocationMethod";
const MISSED_DROP_ZOOM = "missedDropZoom";
const DROP_LOCATION_CONDENSER = "dropLocationCondenser";
const DROP_LOCATION_GAMMA = "dropLocationGamma";
const DROP_LOCATION_POLARIZER = "dropLocationPolarizer";
// const DROP_LOCATION_EXPOSURE = "dropLocationExposure";
// const DROP_LOCATION_GAIN = "dropLocationGain";
// const DROP_LOCATION_BRIGHTNESS = "dropLocationBrightness";
const DROP_LOCATION_BINNING = "dropLocationBinning";
const DROP_LOCATION_ZOOM = "dropLocationZoom";

const getDefaultDropOptionOverrides = (
  numberOfDrop: number,
  minContinousZoom = 1,
  minObjectiveZoom = 1,
  minCropZoom = 1
): DropOverride[] => {
  let newDropOverrides = [] as DropOverride[];

  for (let i = 0; i < numberOfDrop; i++) {
    newDropOverrides.push({
      continuousZoom: minContinousZoom,
      objectiveZoom: minObjectiveZoom,
      cropZoom: minCropZoom,
      roi: {
        autoSelectObjective: true,
        fixedObjective: minObjectiveZoom,
      },
    });
  }

  return newDropOverrides;
};

type Props = {
  style?: React.CSSProperties;
  lightpathCapability: LightPathCapability;
  captureProfile: PlateTypeCaptureProfile;
  nonVisibleCaptureProfiles: PlateTypeCaptureProfile[];
  plateType: PlateType;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function VisibleDropLocation({
  style = {},
  lightpathCapability,
  captureProfile,
  nonVisibleCaptureProfiles,
  setCaptureProfile,
  onErrorCleared,
  onErrorOccur,
  plateType,
}: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const visibleCaptureProperty = captureProfile.visible;
  const dropLocationCaptureProperty = captureProfile.dropLocation;

  if (visibleCaptureProperty == null) {
    throw new Error("Visible capture property is null");
  }

  if (captureProfile.dropLocation == null) {
    throw new Error("Drop location capture property is null");
  }

  if (dropLocationCaptureProperty == null) {
    throw new Error("Drop location capture property is null");
  }
  const locateDropAlgorithmProperty = visibleCaptureProperty.locateDrop;

  if (locateDropAlgorithmProperty == null) {
    throw new Error("Locate drop algorithm property is null");
  }

  const numberOfDropPerWell = plateType.numberOfDropPerWell;
  const useDropLocation = captureProfile.dropLocation.dropLocationMethod !== 0;

  for (var nonVisibleCaptureProfile of nonVisibleCaptureProfiles) {
    if (
      nonVisibleCaptureProfile.dropLocation?.dropLocationMethod !==
      captureProfile.dropLocation.dropLocationMethod
    ) {
      if (nonVisibleCaptureProfile.dropLocation == null) {
        continue;
      }
      setCaptureProfile({
        ...nonVisibleCaptureProfile,
        dropLocation: {
          ...nonVisibleCaptureProfile.dropLocation,
          dropLocationMethod: captureProfile.dropLocation.dropLocationMethod,
        },
      });
    }
  }

  const onDropLocationCapturePropertyChanged = (
    dropLocation: DropLocationCaptureProperty
  ): void => {
    setCaptureProfile({
      ...captureProfile,
      dropLocation: dropLocation,
    });
  };

  const onVisibleCapturePropertyChanged = (
    visibleCaptureProperty: VisibleCaptureProperty
  ): void => {
    setCaptureProfile({
      ...captureProfile,
      visible: visibleCaptureProperty,
    });
  };

  const onLocateDropCapturePropertyChanged = (
    locateDrop: LocateDropAlgorithmProperty
  ): void => {
    setCaptureProfile({
      ...captureProfile,
      visible: {
        ...visibleCaptureProperty,
        locateDrop: locateDrop,
      },
    });
  };

  const onStandardDropLocatorValueChanged = (
    standardDropLocator: StandardDropLocatorCaptureProperty
  ): void => {
    const newDefaultImagingProfile = {
      ...captureProfile,
      visible: {
        ...visibleCaptureProperty,
        locateDrop: {
          ...locateDropAlgorithmProperty,
          standardDropLocator: standardDropLocator,
        },
      },
    };
    setCaptureProfile(newDefaultImagingProfile);
  };

  const onLipidPhaseDropLocatorValueChanged = (
    lipidPhaseDropLocator: LipidPhaseDropLocatorCaptureProperty
  ) => {
    const newDefaultImagingProfile = {
      ...captureProfile,
      visible: {
        ...visibleCaptureProperty,
        locateDrop: {
          ...locateDropAlgorithmProperty,
          lipidPhaseDropLocator: lipidPhaseDropLocator,
        },
      },
    };
    setCaptureProfile(newDefaultImagingProfile);
  };

  const continuousZoom = lightpathCapability.optic.continuousZoom;
  const objectiveZoom = lightpathCapability.optic.objectiveZoom;
  const binning = lightpathCapability.camera.binning;
  const gamma = lightpathCapability.camera.gamma;
  const condenser = lightpathCapability.optic.condenser;
  const polarizer = lightpathCapability.optic.polarizer;
  const cropZoom = lightpathCapability.optic.cropZoom;

  let continuousZoomView = null;
  let missedContinuousZoomView = null;
  let dropLocationContinuousZoomView = null;
  let objectiveZoomView = null;
  let missedObjectiveZoomView = null;
  let dropLocationObjectiveZoomView = null;
  let dropOverrideView = null;
  let cropZoomView = null;

  const dropOptionOverrides =
    captureProfile.dropOverrides !== undefined &&
    captureProfile.dropOverrides !== null &&
    captureProfile.dropOverrides?.length > 0
      ? captureProfile.dropOverrides
      : getDefaultDropOptionOverrides(
          numberOfDropPerWell,
          continuousZoom?.minContinuousZoom,
          objectiveZoom?.objectives[0].value,
          cropZoom?.minCropZoom
        );

  if (continuousZoom) {
    // this code disabled because need to enable the UI and force user to look the Log.
    // if (visibleCaptureProperty.continuousZoom == null) {
    //   throw new Error("Capture profile continuous zoom is null");
    // }

    // if (dropLocationCaptureProperty.missedDropZoom == null) {
    //   throw new Error("Capture profile missed continuous zoom is null");
    // }

    continuousZoomView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.continuousZoom ?? 0}
        onChange={(name, value) =>
          onVisibleCapturePropertyChanged({
            ...visibleCaptureProperty,
            continuousZoom: value,
          })
        }
        label="Zoom"
        name={EOpticAndCameraFeature.ContinuousZoom}
        unit="x"
        min={continuousZoom.minContinuousZoom}
        max={continuousZoom.maxContinuousZoom}
        disabled={
          useDropLocation ||
          captureProfile.dropLocation?.dropOptionOverrideExist
        }
        style={{
          paddingLeft: "13px",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    missedContinuousZoomView = (
      <DefaultImagingSettingTextBox
        value={dropLocationCaptureProperty.missedDropZoom}
        onChange={(name, value) =>
          onDropLocationCapturePropertyChanged({
            ...dropLocationCaptureProperty,
            missedDropZoom: value,
          })
        }
        label="Missed Zoom"
        name={MISSED_DROP_ZOOM}
        unit="x"
        min={continuousZoom.minContinuousZoom}
        max={continuousZoom.maxContinuousZoom}
        disabled={!useDropLocation}
        style={{
          // paddingLeft: "13px",
          marginBottom: "5px",
          gridArea: "missedZoom",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    dropLocationContinuousZoomView = (
      <DefaultImagingSettingTextBox
        value={locateDropAlgorithmProperty.dropLocationZoom}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationZoom: value,
          })
        }
        label="Zoom"
        name={DROP_LOCATION_ZOOM}
        unit="x"
        min={continuousZoom.minContinuousZoom}
        max={continuousZoom.maxContinuousZoom}
        disabled={!useDropLocation}
        style={{
          // paddingLeft: "13px",
          marginBottom: "5px",
          gridArea: "dropLocationZoom",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
    if (plateType.numberOfDropPerWell > 1) {
      dropOverrideView = (
        <ContinousZoomDropOverride
          captureProfile={captureProfile}
          setCaptureProfile={setCaptureProfile}
          plateType={plateType}
          onErrorOccur={onErrorOccur}
          onErrorCleared={onErrorCleared}
          dropOptionOverrides={dropOptionOverrides}
          continuousZoom={continuousZoom}
          disabled={useDropLocation}
        />
      );
    }
  }

  if (objectiveZoom) {
    // this code disabled because need to enable the UI and force user to look the Log.
    // if (visibleCaptureProperty.objectiveZoom == null) {
    //   throw new Error("Capture profile objective zoom is null");
    // }
    // if (dropLocationCaptureProperty.missedDropZoom == null) {
    //   throw new Error("Capture profile missed objective zoom is null");
    // }

    const displayvalues =
      ImagerUtil.getObjectiveZoomDisplayValues(objectiveZoom);

    objectiveZoomView = (
      <DefaultImagingSettingComboBox
        displayValues={displayvalues}
        value={
          visibleCaptureProperty.objectiveZoom
            ? visibleCaptureProperty.objectiveZoom.toFixed(1)
            : ""
        }
        onChange={(name, value) =>
          onVisibleCapturePropertyChanged({
            ...visibleCaptureProperty,
            objectiveZoom: Number.parseFloat(value),
          })
        }
        label="Objective"
        name={EOpticAndCameraFeature.ObjectiveZoom}
        unit="x"
        disabled={
          useDropLocation ||
          captureProfile.dropLocation?.dropOptionOverrideExist
        }
        style={{
          paddingLeft: "13px",
        }}
      />
    );

    missedObjectiveZoomView = (
      <DefaultImagingSettingComboBox
        displayValues={displayvalues}
        value={dropLocationCaptureProperty.missedDropZoom.toFixed(1)}
        onChange={(name, value) =>
          onDropLocationCapturePropertyChanged({
            ...dropLocationCaptureProperty,
            missedDropZoom: Number.parseFloat(value),
          })
        }
        label="Missed Objective"
        name={MISSED_DROP_ZOOM}
        unit="x"
        disabled={!useDropLocation}
        style={{
          // paddingLeft: "13px",
          marginBottom: "5px",
          gridArea: "missedZoom",
        }}
        dropDownStyle={{
          width: 71,
        }}
      />
    );

    dropLocationObjectiveZoomView = (
      <DefaultImagingSettingComboBox
        displayValues={displayvalues}
        value={locateDropAlgorithmProperty.dropLocationZoom.toFixed(1)}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationZoom: Number.parseFloat(value),
          })
        }
        label="Objective"
        name={DROP_LOCATION_ZOOM}
        unit="x"
        disabled={!useDropLocation}
        style={{
          // paddingLeft: "13px",
          marginBottom: "5px",
          gridArea: "dropLocationZoom",
        }}
        dropDownStyle={{
          width: 71,
        }}
      />
    );
    if (plateType.numberOfDropPerWell > 1) {
      dropOverrideView = (
        <ObjectiveZoomDropOverride
          captureProfile={captureProfile}
          setCaptureProfile={setCaptureProfile}
          plateType={plateType}
          onErrorOccur={onErrorOccur}
          onErrorCleared={onErrorCleared}
          dropOptionOverrides={dropOptionOverrides}
          objectiveZoom={objectiveZoom}
          cropZoom={cropZoom}
          disabled={useDropLocation}
          lightPath={ELightPath.Visible}
          lightPathCapability={lightpathCapability}
        />
      );
    }
  }

  if (cropZoom) {
    if (visibleCaptureProperty.cropZoom == null) {
      throw new Error("Capture profile Crop zoom is null");
    }

    cropZoomView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.cropZoom}
        onChange={(name, value) =>
          onVisibleCapturePropertyChanged({
            ...visibleCaptureProperty,
            cropZoom: value,
          })
        }
        label="Zoom"
        name={EOpticAndCameraFeature.ContinuousZoom}
        unit="x"
        min={cropZoom.minCropZoom}
        max={cropZoom.maxCropZoom}
        disabled={
          useDropLocation ||
          captureProfile.dropLocation?.dropOptionOverrideExist
        }
        style={{
          paddingLeft: "13px",
          marginTop: 5,
          marginBottom: 5,
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let gammaView = null;
  if (gamma != null) {
    if (locateDropAlgorithmProperty.dropLocationGamma == null) {
      throw new Error("Capture profile drop location gamma is null");
    }

    gammaView = (
      <DefaultImagingSettingTextBox
        value={locateDropAlgorithmProperty.dropLocationGamma}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationGamma: value,
          })
        }
        label="Gamma"
        name={DROP_LOCATION_GAMMA}
        unit=""
        min={gamma.minGamma}
        max={gamma.maxGamma}
        disabled={!useDropLocation}
        style={{
          gridArea: "gamma",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let condenserView = null;
  if (condenser != null) {
    if (locateDropAlgorithmProperty.dropLocationCondenser == null) {
      throw new Error("Capture profile drop location condenser is null");
    }

    condenserView = (
      <DefaultImagingSettingTextBox
        value={locateDropAlgorithmProperty.dropLocationCondenser}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationCondenser: value,
          })
        }
        label="Condenser"
        name={DROP_LOCATION_CONDENSER}
        unit="%"
        min={condenser.minCondenser}
        max={condenser.maxCondenser}
        disabled={!useDropLocation}
        style={{
          gridArea: "condenser",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let polarizerView = null;
  if (polarizer != null) {
    if (locateDropAlgorithmProperty.dropLocationPolarizer == null) {
      throw new Error("Capture profile drop location polarizer is null");
    }
    polarizerView = (
      <DefaultImagingSettingTextBox
        value={locateDropAlgorithmProperty.dropLocationPolarizer}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationPolarizer: value,
          })
        }
        label="Polarizer"
        name={DROP_LOCATION_POLARIZER}
        unit="°"
        min={polarizer.minPolarizer}
        max={polarizer.maxPolarizer}
        disabled={!useDropLocation}
        style={{
          gridArea: "polarizer",
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  const locateDropMethodDisplayValues = [
    {
      value: "1",
      display: "Standard",
    },
    {
      value: "2",
      display: "Lipidic Cubic Phase",
    },
  ];

  let locateDropAdvancedView = null;

  if (useDropLocation && showAdvanced) {
    if (captureProfile.dropLocation.dropLocationMethod === 1) {
      locateDropAdvancedView = (
        <StandardDropLocatorView
          standardDropLocator={locateDropAlgorithmProperty.standardDropLocator}
          onChange={onStandardDropLocatorValueChanged}
          style={{
            paddingLeft: "13px",
            marginTop: "10px",
          }}
          onErrorCleared={onErrorCleared}
          onErrorOccur={onErrorOccur}
        />
      );
    } else if (captureProfile.dropLocation.dropLocationMethod === 2) {
      locateDropAdvancedView = (
        <LcpDropLocator
          lipidPhaseDropLocator={
            locateDropAlgorithmProperty.lipidPhaseDropLocator
          }
          onChange={onLipidPhaseDropLocatorValueChanged}
          style={{
            paddingLeft: "13px",
            marginTop: "10px",
          }}
          onErrorCleared={onErrorCleared}
          onErrorOccur={onErrorOccur}
        />
      );
    }
  }

  return (
    <div
      style={{
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        ...style,
      }}
    >
      {/* //-------DROP_LOCATE_METHOD_SELECTION-------// */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "baseline",
          width: "100%",
          marginBottom: "10px",
        }}
      >
        <label
          style={{
            fontWeight: "bold",
            height: "100%",
          }}
        >
          Drop Location
        </label>
        <DefaultImagingSettingRadioButton
          name="dropLocationMethod"
          label="Fixed"
          checked={dropLocationCaptureProperty.dropLocationMethod === 0}
          onChange={(name, value) =>
            onDropLocationCapturePropertyChanged({
              ...dropLocationCaptureProperty,
              dropLocationMethod: 0,
            })
          }
          style={{
            paddingLeft: "13px",
          }}
          disabled={false}
        />

        <DefaultImagingSettingRadioButton
          name="dropLocationMethod"
          label="Locate Drop"
          checked={dropLocationCaptureProperty.dropLocationMethod !== 0}
          onChange={(name, value) =>
            onDropLocationCapturePropertyChanged({
              ...dropLocationCaptureProperty,
              dropLocationMethod: 1,
            })
          }
          style={{
            paddingLeft: "13px",
          }}
          disabled={false}
        />
      </div>

      {/* //-------FIXED_DROP_LOCATE_METHOD_ZOOM-------// */}

      <div style={{ margin: "24px 0px", fontWeight: "bold" }}>
        Fixed Method Settings
      </div>

      {continuousZoomView}
      {objectiveZoomView}
      {cropZoomView}
      {dropOverrideView}

      {/* //-------LOCATE_DROP_METHOD_SETTING-------// */}

      <div style={{ margin: "24px 0px", fontWeight: "bold" }}>
        Locate Drop Method Settings
      </div>

      <DefaultImagingSettingComboBox
        displayValues={locateDropMethodDisplayValues}
        value={dropLocationCaptureProperty.dropLocationMethod.toString()}
        onChange={(name, value) =>
          onDropLocationCapturePropertyChanged({
            ...dropLocationCaptureProperty,
            dropLocationMethod: Number.parseInt(value),
          })
        }
        label="Method"
        name={DROP_LOCATION_METHOD}
        unit=""
        disabled={!useDropLocation}
        style={{
          paddingLeft: "13px",
          marginBottom: "5px",
        }}
      />

      <DefaultImagingSettingBinning
        binningDisplayValues={binning.binningDisplayValues}
        value={locateDropAlgorithmProperty.dropLocationBinning}
        name={DROP_LOCATION_BINNING}
        onChange={(name, value) =>
          onLocateDropCapturePropertyChanged({
            ...locateDropAlgorithmProperty,
            dropLocationBinning: value,
          })
        }
        disabled={!useDropLocation}
        style={{
          paddingLeft: "13px",
          marginBottom: "5px",
        }}
      />

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateAreas: `'missedZoom dropLocationZoom'
                              'gamma polarizer' 
                              'condenser advancedButton'`,
          gridTemplateColumns: "50% 50%",
          gridTemplateRows: "auto auto",
          gridRowGap: "5px",
          marginLeft: "0px",
          paddingLeft: "13px",
        }}
      >
        {missedContinuousZoomView}
        {missedObjectiveZoomView}
        {dropLocationContinuousZoomView}
        {dropLocationObjectiveZoomView}
        {condenserView}
        {polarizerView}
        {gammaView}
        <button
          style={{
            width: "76.5%",
            height: "35px",
            padding: "4px",
            border: "1px slid #414042",
            backgroundColor: useDropLocation ? "#414042" : "#A09FA0",
            borderRadius: "4px",
            color: "#ffffff",
            textTransform: "uppercase",
            backgroundImage: "none",
            appearance: "none",
            gridArea: "advancedButton",
          }}
          disabled={!useDropLocation}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          Advanced Settings <span>{"\u25BC"}</span>
        </button>
      </div>
      {locateDropAdvancedView}

      {objectiveZoom && (
        <RoiOptions
          disabled={captureProfile.dropLocation.dropOptionOverrideExist}
          roi={captureProfile.roi}
          onValueChanged={(roi) =>
            setCaptureProfile({ ...captureProfile, roi: roi })
          }
          lightPath={ELightPath.Visible}
          lightpathCapability={lightpathCapability}
          fixedObjectiveDropDownDirection="up"
        />
      )}
    </div>
  );
}
